import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { FilterProvider } from '@/context/FilterContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { SocialLayout } from '@/components/layout/SocialLayout';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';

// Pages
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import Dashboard from '@/pages/Dashboard';
import Charts from '@/pages/Charts';
import Insights from '@/pages/Insights';
import Leads from '@/pages/Leads';
import Forecast from '@/pages/Forecast';
import Dispatch from '@/pages/Dispatch';
// Quotations page removed per user request
import Comparison from '@/pages/Comparison';
import CompareForecasts from '@/pages/CompareForecasts';
import Admin from '@/pages/Admin';
import EntityProfile from '@/pages/EntityProfile';
import TransferredLeads from '@/pages/TransferredLeads';
import DuplicateLeads from '@/pages/DuplicateLeads';
import Tenders from '@/pages/Tenders';
import StockChecker from '@/pages/StockChecker';
import DealerScorecard from '@/pages/DealerScorecard';
import SalesAssistance from '@/pages/SalesAssistance';
import CustomerIntelligence from '@/pages/CustomerIntelligence';
import CustomerProfile from '@/pages/CustomerProfile';
import ChatWidget from '@/components/ChatWidget';
import { useActivityTracker, trackAction } from '@/hooks/useActivityTracker';

// Social Media Pages
import SocialDashboard from '@/pages/social/SocialDashboard';
import ContentUpload from '@/pages/social/ContentUpload';
import ContentDetail from '@/pages/social/ContentDetail';
import TemplatesManager from '@/pages/social/TemplatesManager';
import BlogGenerator from '@/pages/social/BlogGenerator';
import CompetitorAnalysis from '@/pages/social/CompetitorAnalysis';
import WhatsAppBulk from '@/pages/social/WhatsAppBulk';
import SocialAnalytics from '@/pages/social/SocialAnalytics';
import MessagingInbox from '@/pages/social/MessagingInbox';
import Connections from '@/pages/social/Connections';
import ContentCalendar from '@/pages/social/ContentCalendar';
import SecurityDashboard from '@/pages/SecurityDashboard';
import PredictionAccuracy from '@/pages/PredictionAccuracy';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.some(role => hasRole(role))) {
    // SocialMedia users should go to /social, not /dashboard
    if (hasRole('SocialMedia')) {
      return <Navigate to="/social" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect to appropriate page if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) {
    // Redirect based on role
    if (user.role === 'SocialMedia') {
      return <Navigate to="/social" replace />;
    }
    if (user.role === 'TenderUser') {
      return <Navigate to="/tenders" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  useActivityTracker(); // Track page views

  // Check for session_id in URL hash BEFORE rendering routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Protected Routes - Main LMS (excludes SocialMedia role) */}
      <Route element={
        <ProtectedRoute allowedRoles={['Admin', 'Manager', 'Employee', 'TenderUser']}>
          <FilterProvider>
            <MainLayout />
          </FilterProvider>
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/charts" element={<Charts />} />
        <Route path="/insights" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <Insights />
          </ProtectedRoute>
        } />
        <Route path="/leads" element={<Leads />} />
        <Route path="/comparison" element={<Comparison />} />
        {/* Compare Forecasts is now integrated into Forecast page - redirect old URL */}
        <Route path="/compare-forecasts" element={<Navigate to="/forecast" replace />} />
        <Route path="/profile/:entityType/:entityId" element={<EntityProfile />} />
        <Route path="/transferred-leads" element={<TransferredLeads />} />
        <Route path="/duplicate-leads" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <DuplicateLeads />
          </ProtectedRoute>
        } />
        {/* Quotations route removed per user request */}
        
        {/* Manager+ only */}
        <Route path="/forecast" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <Forecast />
          </ProtectedRoute>
        } />
        <Route path="/prediction-accuracy" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <PredictionAccuracy />
          </ProtectedRoute>
        } />
        
        <Route path="/dispatch" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager', 'Employee']}>
            <Dispatch />
          </ProtectedRoute>
        } />
        
        {/* Tender Tracking */}
        <Route path="/tenders" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager', 'TenderUser']}>
            <Tenders />
          </ProtectedRoute>
        } />
        
        {/* Sales Assistance - Admin only */}
        <Route path="/sales-assistance" element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <SalesAssistance />
          </ProtectedRoute>
        } />
        
        {/* Dealer Scorecard - Manager+ only */}
        <Route path="/dealer-scorecard" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <DealerScorecard />
          </ProtectedRoute>
        } />
        
        {/* Customer Intelligence - Manager+ only */}
        <Route path="/customers" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <CustomerIntelligence />
          </ProtectedRoute>
        } />
        <Route path="/customers/:phone" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <CustomerProfile />
          </ProtectedRoute>
        } />
        
        {/* Stock Checker - Manager+ only */}
        <Route path="/stock" element={
          <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
            <StockChecker />
          </ProtectedRoute>
        } />
        
        {/* Admin only */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <Admin />
          </ProtectedRoute>
        } />
        <Route path="/security" element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <SecurityDashboard />
          </ProtectedRoute>
        } />
      </Route>

      {/* Social Media Routes — Isolated layout for SocialMedia role */}
      <Route element={
        <ProtectedRoute allowedRoles={['SocialMedia', 'Admin']}>
          <SocialLayout />
        </ProtectedRoute>
      }>
        <Route path="/social" element={<SocialDashboard />} />
        <Route path="/social/calendar" element={<ContentCalendar />} />
        <Route path="/social/upload" element={<ContentUpload />} />
        <Route path="/social/content/:id" element={<ContentDetail />} />
        <Route path="/social/templates" element={<TemplatesManager />} />
        <Route path="/social/blog" element={<BlogGenerator />} />
        <Route path="/social/competitors" element={<CompetitorAnalysis />} />
        <Route path="/social/whatsapp" element={<WhatsAppBulk />} />
        <Route path="/social/analytics" element={<SocialAnalytics />} />
        <Route path="/social/messaging" element={<MessagingInbox />} />
        <Route path="/social/connections" element={<Connections />} />
      </Route>

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const ChatWidgetWrapper = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <ChatWidget userRole={user.role} userId={user.user_id} />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <ChatWidgetWrapper />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
